import type React from 'react';
import { useMemo } from 'react';
import { useChatLanguage, useChatLocale } from '../../context/ChatThemeContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import RoseLogo from '../../logos/RoseLogo';
import { findBestLocaleValue } from '../../utils/content/localeUtils';

interface ChatDisclaimerProps {
    compact?: boolean; // Force short disclaimer (used in copilot mode)
}

const ChatDisclaimer: React.FC<ChatDisclaimerProps> = ({ compact = false }) => {
    const userLanguage = useChatLanguage();
    const userLocale = useChatLocale();
    const { currentDomain, currentResolver } = useSiteConfig();

    const { ai, poweredBy, aiMobile, poweredByMobile, roseWebsiteUrl } = useMemo(() => {
        // Default disclaimers
        const defaultI18n = {
            en: { ai: 'AI-generated replies', poweredBy: 'Powered by' },
            fr: { ai: "Réponses générées par l'IA", poweredBy: 'Propulsé par' },
            es: { ai: 'Respuestas generadas por IA', poweredBy: 'Impulsado por' },
            de: { ai: 'KI-generierte Antworten', poweredBy: 'Unterstützt von' },
            it: { ai: "Risposte generate dall'IA", poweredBy: 'Offerto da' },
            bg: { ai: 'Отговори, генерирани от AI', poweredBy: 'С подкрепата на' },
            nl: { ai: 'AI-gegenereerde antwoorden', poweredBy: 'Mogelijk gemaakt door' },
            ro: { ai: 'Răspunsuri generate de IA', poweredBy: 'Oferit de' },
            hu: { ai: 'MI által generált válaszok', poweredBy: 'Működteti' },
        } as const;

        const lang = ((userLanguage || 'en').toLowerCase() as keyof typeof defaultI18n) || 'en';
        const defaultForLang = defaultI18n[lang] ?? defaultI18n.en;
        const roseUrl = new URL('https://userose.ai/');
        roseUrl.searchParams.set('utm_source', 'watermark');
        roseUrl.searchParams.set('utm_medium', 'referral');
        if (currentDomain) {
            roseUrl.searchParams.set('utm_campaign', currentDomain);
        }

        // Check if there are custom disclaimers for this language
        const customDisclaimers = currentResolver?.appearance.disclaimers?.text;
        const matchedCustomDisclaimer = findBestLocaleValue(customDisclaimers, userLocale);

        if (matchedCustomDisclaimer) {
            return {
                // Desktop: use custom disclaimers
                ai: matchedCustomDisclaimer.ai,
                poweredBy: matchedCustomDisclaimer.poweredBy,
                // Mobile: use default disclaimers (shorter)
                aiMobile: defaultForLang.ai,
                poweredByMobile: defaultForLang.poweredBy,
                roseWebsiteUrl: roseUrl.toString(),
            };
        }

        // Fall back to default disclaimers for both
        return {
            ai: defaultForLang.ai,
            poweredBy: defaultForLang.poweredBy,
            aiMobile: defaultForLang.ai,
            poweredByMobile: defaultForLang.poweredBy,
            roseWebsiteUrl: roseUrl.toString(),
        };
    }, [currentDomain, currentResolver, userLanguage, userLocale]);
    // When compact mode is enabled (copilot), always use the short/mobile disclaimers
    const displayAi = compact ? aiMobile : null;
    const displayPoweredBy = compact ? poweredByMobile : null;

    return (
        <div
            className="rose-widget-disclaimer w-full h-4 flex items-center justify-center gap-1.5 text-xs leading-4 select-none"
            data-ph-capture-attribute-rw_disclaimer="true"
            style={{ color: 'var(--rw-text-disclaimer)' }}
        >
            {compact ? (
                // Compact mode: always show short disclaimers
                <>
                    <span>{displayAi}</span>
                    <span aria-hidden="true">•</span>
                    <span>{displayPoweredBy}</span>
                </>
            ) : (
                // Normal mode: responsive disclaimers
                <>
                    <span className="sm:hidden">{aiMobile}</span>
                    <span className="hidden sm:inline">{ai}</span>
                    <span aria-hidden="true">•</span>
                    <span className="sm:hidden">{poweredByMobile}</span>
                    <span className="hidden sm:inline">{poweredBy}</span>
                </>
            )}
            <a
                href={roseWebsiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Rose website"
                className="inline-flex items-center align-middle"
            >
                <RoseLogo width={44} height={13} color="var(--rw-text-disclaimer)" />
            </a>
        </div>
    );
};

export default ChatDisclaimer;
